// 危險操作確認（SPEC #ux-principles UX-03）：自訂對話框，標題寫後果、內文寫影響範圍、主按鈕寫動作本身（不寫「確定」），預設焦點在取消。
// 第 2 級：{ title, body, action }；第 3 級（不可逆）另帶 typeToConfirm = 對象名稱，要輸入一樣的字才能按。
// 主按鈕顏色看動作是不是破壞性、不看級別（UX-03「紅色 = 破壞性」，kinio/app/vms#22）：第 3 級一律紅色；第 2 級的刪除 / 撤銷 / 移除 / 停用…
// 帶 danger: true 才是紅色，其餘（重新開機、套用、改派…）主色。confirmDanger.test.ts 擋住破壞性動作字沒標 danger。
// 用法：const { ask, dialog } = useConfirm(); if (!(await ask({ title, body, action }))) return; … 把 {dialog} 放進畫面。
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from './Dialog';

export interface ConfirmOptions {
  /** 後果（對話框標題） */
  title: string;
  /** 影響範圍 / 改了什麼 */
  body?: ReactNode;
  /** 主按鈕：動作本身 */
  action: string;
  /** 第 3 級：要輸入這個名稱才能按（設備名、政策名…），主按鈕紅色 */
  typeToConfirm?: string;
  /** 破壞性動作（拿掉東西或讓人失去存取：刪除、撤銷、移除、解除、停用、終止、結束、捨棄、離開、登出其他裝置…）：主按鈕紅色 */
  danger?: boolean;
}

export function useConfirm(): { ask: (o: ConfirmOptions) => Promise<boolean>; dialog: ReactNode } {
  const [cur, setCur] = useState<(ConfirmOptions & { resolve: (ok: boolean) => void }) | null>(null);
  const ask = useCallback((o: ConfirmOptions) => new Promise<boolean>((resolve) => setCur({ ...o, resolve })), []);
  const done = (ok: boolean) => {
    cur?.resolve(ok);
    setCur(null);
  };
  return { ask, dialog: cur ? <ConfirmBody o={cur} done={done} /> : null };
}

function ConfirmBody({ o, done }: { o: ConfirmOptions; done: (ok: boolean) => void }) {
  const { t } = useTranslation();
  const cancel = useRef<HTMLButtonElement>(null);
  const [typed, setTyped] = useState('');
  useEffect(() => cancel.current?.focus(), []);
  const lv3 = o.typeToConfirm !== undefined;
  const danger = lv3 || !!o.danger;
  return (
    <Dialog open title={o.title} onClose={() => done(false)}>
      <div data-testid="confirm-dialog" data-level={lv3 ? 3 : 2} data-danger={danger ? 1 : undefined}>
        {o.body && <div className="confirm-body">{o.body}</div>}
        {lv3 && (
          <label className="field">
            <span>{t('common.confirm_type', { name: o.typeToConfirm })}</span>
            <input className="input" value={typed} onChange={(e) => setTyped(e.target.value)} aria-label={t('common.confirm_type', { name: o.typeToConfirm })} />
          </label>
        )}
        <div className="dialog-actions">
          <button ref={cancel} className="btn" onClick={() => done(false)}>
            {t('common.cancel')}
          </button>
          <button className={danger ? 'btn btn-danger' : 'btn btn-primary'} disabled={lv3 && typed.trim() !== o.typeToConfirm} onClick={() => done(true)}>
            {o.action}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
